import * as fs from 'fs';
import {
    addonSerialize,
    log,
    addonDeserialize,
    addon,
    BaseAddon,
    UserToken,
    NullablePromise
} from "pao-aop";
import { Session } from '../session/index';

/**
 * 默认文件编码器
 */
const defaultFileEncoding = 'utf-8';

/**
 * 服务器状态
 */
export type ServerStatus = 'stopped' | 'running' | 'error' | string;

/**
 * 移除文件中的BOM
 * @author pao
 * @param bin
 */
export function removeBOM(bin: Buffer) {
    if (bin[0] === 0xEF && bin[1] === 0xBB && bin[2] === 0xBF) {
        bin = bin.slice(3);
    }
    return bin;
}

/**
 * 保存
 * @author pao
 * @param filePath 文件路径
 */
export function saveObject(filePath: string, obj: Object): void {
    let configString = addonSerialize(obj);
    fs.writeFileSync(filePath, configString, defaultFileEncoding);

    log('file', `配置文件:${filePath}保存完毕`);
}

/**
 * 读取
 * @author pao
 * @param filePath 文件路径
 */
export function loadObject(filePath: string): Object {
    let bin = fs.readFileSync(filePath);
    // 移除BOM
    bin = removeBOM(bin);
    let buffer = bin.toString(defaultFileEncoding);
    let obj = addonDeserialize(buffer);

    log('file', `配置文件:${filePath}读取完毕`);

    return obj;
}

/**
 * 准备配置
 * @author pao
 * @param defaultConfig 默认配置对象
 * @param configFile 配置文件
 * @param loadConfig 是否从配置文件加载
 * @param saveConfig 是否保存配置文件
 */
export function prepareConfig(
    defaultConfig: Object,
    configFile: string,
    loadConfig: boolean = false,
    saveConfig: boolean = true): Object {
    let configObject: Object;
    // 加载应用程序配置
    if (loadConfig) {
        configObject = loadObject(configFile);
    } else {
        configObject = defaultConfig;
        if (saveConfig) {
            saveObject(configFile, configObject);
        }
    }
    return configObject;
}

/**
 * 服务信息: 包含服务名称和服务对象的信息
 * @author pao
 */
@addon('ServerInfo', '服务信息', '包含服务名称和服务对象的信息')
export class ServerInfo {
    constructor(
        public serverName?: string,
        public serverObject?: BaseServer) {

    }
}

/**
 * 全局服务器列表
 * @author pao
 */
let GlobalServers = new Map<string, BaseServer>();
/**
 * 注册服务列表
 * @author pao
 * @param serverInfos 服务信息列表
 */
export function registerServers(serverInfos: ServerInfo[]) {
    for (var serverInfo of serverInfos) {
        registerServer(serverInfo);
    }
}
/**
 * 注册服务
 * @author pao
 * @param serverInfo 服务信息
 */
export function registerServer(serverInfo: ServerInfo) {
    if (!GlobalServers.get(serverInfo.serverName!)) {
        log('server', `服务注册，服务名称：${serverInfo.serverName}`);
        GlobalServers.set(serverInfo.serverName!, serverInfo.serverObject!);
    }
}

/**
 * 编历服务器
 * @param serviceFunction 服务处理方法
 */
export function forEachServer(serverFunction: (serverInfo: BaseServer) => void) {
    GlobalServers.forEach(serverFunction);
}

/** 根据名称获取服务
 * @author pao
 * @param name 名称
 */
export function getServerByName(name: string) {
    return GlobalServers.get(name);
}

/**
 * 服务信息: 包含服务名称和服务对象的信息
 * @author pao
 */
@addon('ServiceInfo', '服务信息', '包含服务名称和服务对象的信息')
export class ServiceInfo {
    constructor(
        public serviceName?: string,
        public serviceObject?: BaseService) {

    }
}

/**
 * 服务授权接口
 */
export interface IServiceAuthority {
    /**
     * 检查授权
     * @param userToken 用户令牌
     * @param functionName 函数名称
     * @param args 参数
     */
    checkAuthority?(token: UserToken, session: Session, functionName: string, args: any[]): Promise<void>;
}

/**
 * 名称:基础服务
 * @description 所有服务的基类
 * @author huyl
 */
@addon('BaseService', '基础服务', '所有服务的基类')
export class BaseService extends BaseAddon {
    /**
     * 授权对象
     */
    public authoryObject?: IServiceAuthority;

    /**
     * 当前用户令牌
     */
    public userToken?: UserToken;

    /**
     * 当前会话
     */
    public session?: Session;

    /** 基础服务 */
    constructor() {
        super();
    }

    /** 调用 */
    async call?(userToken: UserToken, session: Session, functionName: string, args: any[]): Promise<any> {
        // 找不到方法
        if (!this[functionName]) {
            throw new Error(`当前服务没有[${functionName}]的方法`);
        }
        // 当前对象闭包
        let that = this;
        // 设置用户令牌和会话
        this.userToken = userToken;
        this.session = session;
        await this.beforeCallFunction!(userToken, session, functionName, args);

        log('service', `开始调用函数${functionName}`);
        let result = await that[functionName].apply(that, args);
        let resultString = (result || 'undefined').toString().substr(0, 50);
        log('service', `调用函数${functionName}结束，返回:${resultString}`);

        return result;
    }

    /**
     * 函数调用前
     * @param userToken 用户令牌
     * @param session 会话
     * @param functionName 函数名称
     * @param args 参数
     */
    protected async beforeCallFunction?(userToken: UserToken, session: Session, functionName: string, args: any[]): Promise<void> {
        // 验证令牌权限
        try {
            if (!this.authoryObject) {
                /** 如果未定义授权对象，则默认为允许调用 */
                return;
            }
            // 默认返回true,具有权限
            await this.authoryObject.checkAuthority(userToken, session, functionName, args);
        } catch (error) {
            throw new Error(`当前用户执行方法${functionName}时授权失败, 错误:${error}`);
        }
    }
}

/**
 * 名称:基础服务器
 * @description 所有服务器的基类，服务器是可以启动和停止的应用程序，服务器程序不应当阻塞主线程
 * @author pao
 */
@addon('BaseServer', '基础服务器', '所有服务器的基类')
export class BaseServer extends BaseAddon {
    /**
     * 服务器状态
     */
    public status?: ServerStatus = 'stopped';
    /**
     * 服务启动
     */
    start?(): void {
        try {
            this.onStart();
            this.status = 'running';
        } catch (error) {
            this.status = `error: ${error}`;
        }
    }

    /**
     * 服务停止
     */
    stop?(): void {
        try {
            this.onStop();
            this.status = 'stopped';
        } catch (error) {
            this.status = `error: ${error}`;
        }
    }

    protected onStart?(): void {

    }

    protected onStop?(): void {

    }

    /**
     * 服务重置
     */
    reset?(): void {
        this.stop!();
        this.start!();
    }
}

/**
 * 服务数据
 */
export class ServerData {
    /**
     * 服务ID
     */
    id?: string;
    /**
     * 服务名称
     */
    name?: string;
    /**
     * 服务类型
     */
    type?: 'DeviceServer' | 'WebServer' | 'ProxyClient' | 'ProxyServer' | 'ProxyListenServer' | 'DataProcessServer' | string;
    /**
     * 状态
     */
    status?: ServerStatus;
}

/**
 * 名称: 服务管理接口
 * @description 提供服务管理功能的接口
 * @author pao
 */
@addon('IServerManagement', '服务管理接口', '提供服务管理功能的接口')
export class IServerManagement extends BaseAddon {
    /**
     * 获取服务器列表
     */
    getServers(): NullablePromise<ServerData[]> {
        return undefined;
    }
    /**
     * 获取服务明细信息
     */
    getServerDetailInfo(): NullablePromise<object[]> {
        return undefined;
    }
    /**
     * 启动服务
     */
    startServer(serverName: string): NullablePromise<void> {
        return undefined;
    }
    /**
     * 停止服务
     */
    stopServer(serverName: string): NullablePromise<void> {
        return undefined;
    }
}